///
//==============================================================================
// BASIC ENUMS AND CONSTANTS
//==============================================================================
import { PluginApprovalStatus } from "../plugins"

/**
 * User roles in the plugin system
 * This enum should be used consistently throughout the codebase instead of string literals
 * Example: UserRole.GUEST instead of "guest"
 */
export enum UserRole {
  GUEST = "guest",
  HOST_EVENT_VIEW = "host",
  HOST_CONFIG_VIEW = "admin",
  CUSTOM = "custom",
}

/**
 * String literal type for role strings to ensure type safety
 */
export type RoleString =
  | "guest"
  | "host"
  | "admin"
  | "host_event_view"
  | "host_config_view"

/**
 * Standard file names for different view components
 */
export enum ViewFileName {
  GUEST = "GuestView.tsx",
  HOST_EVENT_VIEW = "HostEventView.tsx",
  HOST_CONFIG_VIEW = "HostConfigView.tsx",
  UNKNOWN = "Unknown.tsx",
}

/**
 * Maps UserRole to corresponding ViewFileName
 */
export const ROLE_TO_FILE_NAME: Record<UserRole, ViewFileName> = {
  [UserRole.GUEST]: ViewFileName.GUEST,
  [UserRole.HOST_EVENT_VIEW]: ViewFileName.HOST_EVENT_VIEW,
  [UserRole.HOST_CONFIG_VIEW]: ViewFileName.HOST_CONFIG_VIEW,
  [UserRole.CUSTOM]: ViewFileName.UNKNOWN,
}

/**
 * Maps ViewFileName to corresponding UserRole
 */
export const FILE_NAME_TO_ROLE: Record<ViewFileName, UserRole | undefined> = {
  [ViewFileName.GUEST]: UserRole.GUEST,
  [ViewFileName.HOST_EVENT_VIEW]: UserRole.HOST_EVENT_VIEW,
  [ViewFileName.HOST_CONFIG_VIEW]: UserRole.HOST_CONFIG_VIEW,
  [ViewFileName.UNKNOWN]: undefined,
}

/**
 * Standard file extensions
 */
export const FILE_EXTENSIONS = {
  TYPESCRIPT: ".tsx",
  JSON: ".json",
} as const

/**
 * Constant for metadata file name
 */
export const METADATA_FILE_NAME = "manifest.json"

/**
 * Gets the full file name for a view type
 * @param pattern The ViewFileNamePattern to get the file name for
 * @returns The complete file name with extension
 */
export function getViewFileName(pattern: ViewFileName): string {
  return `${pattern}${FILE_EXTENSIONS.TYPESCRIPT}`
}

/**
 * Determines the file type based on the file name
 * @param fileName The name of the file to check
 * @returns The UserRole corresponding to the file type, "metadata" for manifest files, or undefined
 */
export function getFileTypeFromName(
  fileName: string,
): UserRole | "metadata" | undefined {
  // Check for metadata first
  if (fileName.includes(METADATA_FILE_NAME)) {
    return "metadata"
  }

  // Check each view file name
  for (const [viewFileName, role] of Object.entries(FILE_NAME_TO_ROLE)) {
    if (fileName.includes(viewFileName.replace(".tsx", ""))) {
      return role
    }
  }

  // Check if it's a custom view - if so, we need to find the actual role from the plugin
  if (isCustomView({ name: fileName } as PluginFile)) {
    // For custom views, we can't determine the role just from the filename
    // We need to return a special value that indicates this is a custom view
    // The calling code will need to look up the actual role from the plugin's files
    return "custom" as any // This is a temporary solution - we'll handle this in the calling code
  }

  return undefined
}

/**
 * Gets the default file name for a given role
 * @param role The UserRole to get the file name for
 * @returns The default file name for the role
 */
export function getDefaultFileNameForRole(role: UserRole): string {
  return ROLE_TO_FILE_NAME[role]
}

/**
 * Checks if a file is a custom view (not one of the standard views)
 * @param file The plugin file to check
 * @returns true if the file is a custom view
 */
export function isCustomView(file: PluginFile): boolean {
  const viewName = file?.name?.replace?.(".tsx", "")
  // Check if it's not one of the standard view names
  return (
    viewName !== "GuestView" &&
    viewName !== "HostEventView" &&
    viewName !== "HostConfigView"
  )
}

/**
 * Status states for the code transpilation process
 */
export type TranspileStatus = "idle" | "processing" | "updated" | "error"

/**
 * Error types for the plugin studio system
 */
export enum PluginErrorType {
  VALIDATION = "validation",
  TRANSPILATION = "transpilation",
  RUNTIME = "runtime",
  NETWORK = "network",
  PERMISSION = "permission",
  UNKNOWN = "unknown",
}

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  INFO = "info",
  WARNING = "warning",
  ERROR = "error",
  CRITICAL = "critical",
}

/**
 * Structured error object for consistent error handling
 */
export interface PluginError {
  type: PluginErrorType
  message: string
  severity: ErrorSeverity
  code?: string
  details?: Record<string, any>
  timestamp: number
}

/**
 * Event types that can occur in the plugin lifecycle
 */
export enum PluginEventType {
  LOAD = "load",
  INIT = "init",
  UPDATE = "update",
  REFRESH = "refresh",
  SAVE = "save",
  ERROR = "error",
}

//==============================================================================
// PLUGIN STRUCTURE AND CODE TYPES
//==============================================================================

/**
 * Individual file in the plugin's file system
 */
export interface PluginFile {
  id: string
  name: string
  path: string
  content: string
  transpiledContent?: string
  type: UserRole | "other"
  lastModified: number
}

/**
 * Code structure for a plugin aligned with backend expectations
 */
export interface PluginCode {
  frontendGuestCodeWeb: string
  frontendHostViewCodeWeb: string
  frontendHostConfigCodeWeb: string
  transpiledFrontendGuestCodeWeb?: string
  transpiledFrontendHostViewCodeWeb?: string
  transpiledFrontendHostConfigCodeWeb?: string
  customViews?: {
    [viewName: string]: {
      sourceCode?: string
      transpiledCode?: string
      visibleToRole?: string
      fileName?: string
    }
  }
}

/**
 * Complete plugin definition including metadata and code
 */
export interface Plugin {
  id: string
  name: string
  description: string
  manifest: {
    name: string
    description: string
    version: string
    author: string
    category?: string
    tags?: string[]
    requiresZeroConfiguration?: boolean
    requiresAuthentication?: boolean
    approvalStatus?: PluginApprovalStatus
    iconName?: string
    showHostViewInDetails?: boolean
    requiresJoinToView?: boolean
  }
  files: PluginFile[]
  code?: PluginCode
  lastSaved?: number
  createdAt: number
  isNew?: boolean // Flag to indicate if this is a new plugin that needs to be created
}

/**
 * Prefix used for temporary IDs of new plugins before they are saved to the server
 */
export const NEW_PLUGIN_ID_PREFIX = "new-plugin-"

/**
 * File system representation for UI display
 */
export interface FileSystemItem {
  id: string
  name: string
  type: "folder" | "file"
  path: string
  role?: UserRole | "other"
  children?: FileSystemItem[]
}

/**
 * Organized plugin file structure
 */
export interface PluginStructure {
  id: string
  name: string
  path: string
  folders: {
    backend: FileSystemItem[]
    frontend: FileSystemItem[]
    metadata: FileSystemItem[]
  }
}

//==============================================================================
// TEST AND LIVE DATA TYPES
//==============================================================================

/**
 * Plugin configuration data type
 */
export interface PluginConfigData {
  [key: string]: string | number | boolean | object
}

/**
 * Event data structure for plugins
 */
export interface EventData {
  id?: string
  title?: string
  date?: string | Date
  location?: {
    name?: string
    address?: string
    coordinates?: {
      latitude: number
      longitude: number
    }
    [key: string]: any
  }
  description?: string
  attendees?: {
    count?: number
    list?: Array<{
      id: string
      name?: string
      role?: UserRole
      [key: string]: any
    }>
  }
  startTime?: string | Date
  endTime?: string | Date
  timezone?: string
  status?: "scheduled" | "active" | "completed" | "cancelled"
  organizer?: {
    id: string
    name?: string
    [key: string]: any
  }
  settings?: Record<string, any>
  [key: string]: any // Allowing for flexible event properties
  flyerImage?: string
}

/**
 * Plugin data attached to an event
 *
 * SYNC REQUIREMENT: This interface must be kept in sync with the Monaco editor type definitions
 * in apps/website/src/@fuse/pages/plugins/types/pluginEditorTypes.ts (PluginAPI.EventPluginData interface)
 *
 * When modifying this interface:
 * 1. Update the corresponding type string in pluginEditorTypes.ts
 * 2. Ensure property names and types match exactly
 * 3. Maintain the same optional/required property structure
 */
export interface EventPluginData {
  settings?: {
    enabled: boolean
    [key: string]: any
  }
  state?: {
    [key: string]: any
  }
  [key: string]: any
}

/**
 * User-specific plugin data
 *
 * SYNC REQUIREMENT: This interface must be kept in sync with the Monaco editor type definitions
 * in apps/website/src/@fuse/pages/plugins/types/pluginEditorTypes.ts (PluginAPI.UserPluginData interface)
 *
 * When modifying this interface:
 * 1. Update the corresponding type string in pluginEditorTypes.ts
 * 2. Ensure property names and types match exactly
 * 3. Maintain the same optional/required property structure
 */
export interface UserPluginData {
  preferences?: {
    [key: string]: any
  }
  permissions?: string[]
  state?: {
    [key: string]: any
  }
  [key: string]: any
}

/**
 * Definition of a test case for plugin testing
 */
export interface TestCase {
  id: string
  name: string
  eventData: EventData
  pluginConfig: PluginConfigData
  eventPluginData: EventPluginData
  settings: {
    triggerDelay: number
    simulateNetworkDelay: boolean
  }
}

/**
 * User data in a test case
 */
export interface UserData {
  displayName?: string
  email?: string
  preferences?: {
    [key: string]: any
  }
  [key: string]: any
}

/**
 * User in a test case
 */
export interface TestCaseUser {
  id: string
  name: string
  role: UserRole
  userData: UserData
  userPluginData: UserPluginData
  viewAs?: UserRole // Optional for backward compatibility; defaults to role if not specified
}

// Add opaque type aliases for plugin IDs at the top after imports
export type CodeEditorPluginId = string & { readonly __brand: unique symbol }
export type LivePreviewPluginId = string & { readonly __brand: unique symbol }

// Functions to safely convert string IDs to typed IDs
export function toCodeEditorPluginId(id: string): CodeEditorPluginId {
  return id as CodeEditorPluginId
}

export function toLivePreviewPluginId(id: string): LivePreviewPluginId {
  return id as LivePreviewPluginId
}

/**
 * Live data for plugin runtime testing
 */
export interface LiveData {
  sourceTestCaseId: string | null
  eventData: EventData
  pluginConfig: PluginConfigData
  eventPluginData: EventPluginData
  users: TestCaseUser[]
  activeUserId: string | null
  livePreviewPluginId: LivePreviewPluginId | null // Renamed for clarity
  isDirty: boolean
}

//==============================================================================
// STATE MANAGEMENT TYPES
//==============================================================================

/**
 * UI panel state definition
 */
export interface PanelState {
  visible: boolean
  width?: string
  height?: string
}

/**
 * Complete UI state for the studio
 */
export interface PluginStudioUIState {
  panels: {
    fileExplorer: PanelState
    testPanel: PanelState
    liveDataPanel: PanelState
    previewPanel: PanelState
  }
}

/**
 * State for a single file in the editor
 *
 * Note on activeViewType:
 * - Currently represents which view (guest/host/hostconfig) is being displayed/edited
 * - In future refactoring (pending approval), this would move to user data
 * - The view could then be tied to the user's actual role with an option to "view as" other roles
 */
export interface PluginFileState {
  activeFileId: string | null
  openFileIds: string[]
  activeViewType: UserRole // Using UserRole enum
}

/**
 * State for the code editor component
 */
export interface CodeEditorState {
  currentCode: string
  transpiledCode: string
  showTranspiledCode: boolean // Moved from PluginStudioState to CodeEditorState since it's only needed here
  viewType?: UserRole // The current view type (guest/host/...) being edited
}

/**
 * Top-level state for the entire Plugin Studio
 */
export interface PluginStudioState {
  // Plugin management
  plugins: Plugin[]
  pluginStates: Record<string, PluginFileState>

  // Active plugin reference
  codeEditorPluginId: CodeEditorPluginId | null // Renamed for clarity

  // Test management
  testCases: TestCase[]
  activeTestCaseId: string | null
  liveData: LiveData

  // UI state
  uiState: PluginStudioUIState
  isPreviewStale: boolean
}

/**
 * Gets the currently active plugin ID for code editor
 * @param state The plugin studio state
 * @returns The active plugin ID for code editor or null if none selected
 */
export function getCodeEditorPluginId(
  state: PluginStudioState,
): CodeEditorPluginId | null {
  console.log(`[d2] Reading codeEditorPluginId: ${state.codeEditorPluginId}`)
  return state.codeEditorPluginId
}

/**
 * Gets the currently active plugin ID for live preview
 * @param state The plugin studio state
 * @returns The active plugin ID for live preview or null if none selected
 */
export function getLivePreviewPluginId(
  state: PluginStudioState,
): LivePreviewPluginId | null {
  console.log(
    `[d2] Reading livePreviewPluginId: ${state.liveData.livePreviewPluginId}`,
  )
  return state.liveData.livePreviewPluginId
}

/**
 * Gets the active plugin for code editor purposes
 * @param state The plugin studio state
 * @returns The active plugin for code editor or null if none selected
 */
export function getCodeEditorPlugin(state: PluginStudioState): Plugin | null {
  const activeId = getCodeEditorPluginId(state)
  if (!activeId) return null
  return state.plugins.find((plugin) => plugin.id === activeId) || null
}

/**
 * Sets the active plugin ID for code editor purposes
 * @param state The current state
 * @param pluginId The plugin ID to set as active
 * @returns Updated state with new active plugin ID
 */
export function setCodeEditorPluginId(
  state: PluginStudioState,
  pluginId: string | CodeEditorPluginId | null,
): PluginStudioState {
  console.log(`[d2] Setting codeEditorPluginId to: ${pluginId}`)
  return {
    ...state,
    codeEditorPluginId: pluginId
      ? toCodeEditorPluginId(pluginId as string)
      : null,
  }
}

/**
 * Sets the active plugin ID for live preview purposes
 * @param state The current state
 * @param pluginId The plugin ID to set as active for live preview
 * @returns Updated state with new live active plugin ID
 */
export function setLivePreviewPluginId(
  state: PluginStudioState,
  pluginId: string | LivePreviewPluginId | null,
): PluginStudioState {
  console.log(`[d2] Setting livePreviewPluginId to: ${pluginId}`)
  return {
    ...state,
    liveData: {
      ...state.liveData,
      livePreviewPluginId: pluginId
        ? toLivePreviewPluginId(pluginId as string)
        : null,
    },
  }
}

//==============================================================================
// COMPONENT PROPS TYPES
//==============================================================================

/**
 * Base props that all panels share
 */
export interface PanelBaseProps {
  title?: string
  className?: string
  onToggleVisibility?: () => void
  isVisible?: boolean
}

/**
 * Props for the file explorer component
 */
export interface FileExplorerProps extends PanelBaseProps {
  plugins: Plugin[] | null | undefined
  activePluginId?: string
  onSelectPlugin: (plugin: Plugin) => void
  onSelectFile: (plugin: Plugin, fileId: string) => void
  activeFilePath?: string
  onCreateNewPlugin?: () => void
  showExamplesModal?: boolean
  onShowExamplesModal?: (show: boolean) => void
}

/**
 * Props for the studio layout container
 */
export interface StudioLayoutProps {
  // Component nodes
  fileExplorer: React.ReactNode
  header: React.ReactNode
  codeEditor: React.ReactNode
  testDataPanel: React.ReactNode
  liveDataPanel: React.ReactNode
  previewPanel: React.ReactNode
  breadcrumbs: React.ReactNode

  // Visibility flags
  fileExplorerVisible: boolean
  testPanelVisible: boolean
  liveDataVisible: boolean
  previewVisible: boolean

  // Toggle functions
  onToggleFileExplorer: () => void
  onToggleTestPanel: () => void
  onToggleLiveData: () => void
  onTogglePreview: () => void
}

/**
 * Props for the header panel component
 */
export interface HeaderPanelProps extends PanelBaseProps {
  activePlugin: Plugin | null
  onEditMetadata: () => void
}

/**
 * Props for the file breadcrumbs component
 */
export interface FileBreadcrumbsProps {
  activePlugin: Plugin | null
  activeFile: PluginFile | null
}

/**
 * Props for the live data panel UI component
 */
export interface LiveDataPanelProps extends PanelBaseProps {
  liveData: LiveData
  activeLiveUser: TestCaseUser | null
  onUserChange: (userId: string) => void
  onEventDataChange: (data: EventData) => void
  onPluginConfigChange: (config: PluginConfigData) => void
}

/**
 * Props for the code editor panel UI component
 */
export interface CodeEditorPanelProps extends PanelBaseProps {
  // Core state
  editorState: CodeEditorState
  activePlugin: Plugin | null

  // Status flags
  loading: boolean
  isPreviewStale: boolean
  transpileStatus: TranspileStatus
  showTranspiledCode: boolean

  // Event handlers
  onCodeTabChange: (tab: UserRole | string, file?: any) => void
  onCodeChange: (newCode: string) => void
  onToggleTranspiledView: () => void
  onRunTranspile: () => void

  // Refs
  editorRef: React.MutableRefObject<any>
}

/**
 * Preview plugin data structure
 */
export interface PreviewPluginData {
  id: string
  name: string
  files: PluginFile[]
  manifest: {
    name: string
    description: string
    version: string
    author: string
    category?: string
    tags?: string[]
  }
  code?: PluginCode
  // Other plugin properties needed for preview
  [key: string]: any
}

/**
 * Studio state for preview
 */
export interface PreviewStudioState {
  plugins: Plugin[]
  liveData: LiveData
  eventId?: string | null
  userId?: string | null
  // Functions that might be called by the plugin renderer
  getPluginConfig: () => Promise<any>
  savePluginConfig: (config: any) => Promise<void>
  getPluginEventInstanceData: () => Promise<any>
  savePluginEventInstanceData: (data: any) => Promise<void>
  getEventPluginUserData: () => Promise<any>
  saveEventPluginUserData: (data: any, userId?: string) => Promise<void>
  // Other state properties needed for preview
  [key: string]: any
}

/**
 * Props for the preview panel UI component
 */
export interface PreviewPanelProps extends PanelBaseProps {
  isStale?: boolean
  viewType: UserRole
  transpiledCode: string
  onRefreshPreview?: () => void
  lastAutoUpdate?: number
  studioState: PreviewStudioState
  studioApi: any // RawPluginApi - added to pass to PluginRenderer
  previewPlugin: PreviewPluginData
  eventData: EventData
  activeLiveUser: TestCaseUser | null
  eventDataErrors?: PluginError[]
  userCanViewAsCurrentType?: boolean
}

/**
 * Helper functions for working with UserRole enum
 */

/**
 * Converts a string to a UserRole enum value
 * @param role String representation of role
 * @returns UserRole enum value or undefined if invalid
 */
export function stringToUserRole(
  role?: RoleString | string,
): UserRole | undefined {
  switch (role?.toLowerCase()) {
    case UserRole.GUEST:
      return UserRole.GUEST
    case UserRole.HOST_EVENT_VIEW:
      return UserRole.HOST_EVENT_VIEW
    case UserRole.HOST_CONFIG_VIEW:
      return UserRole.HOST_CONFIG_VIEW
    default:
      return undefined
  }
}

/**
 * Converts a UserRole enum value to its string representation
 * @param role UserRole enum value
 * @returns String representation of the role
 */
export function userRoleToString(role: UserRole): RoleString {
  switch (role) {
    case UserRole.GUEST:
      return UserRole.GUEST
    case UserRole.HOST_EVENT_VIEW:
      return UserRole.HOST_EVENT_VIEW
    case UserRole.HOST_CONFIG_VIEW:
      return UserRole.HOST_CONFIG_VIEW
    default:
      return UserRole.GUEST // Default to guest for safety
  }
}

/**
 * Checks if a string is a valid UserRole
 * @param role String to check
 * @returns true if the string is a valid UserRole
 */
export function isValidUserRole(role?: string): role is RoleString {
  return role !== undefined && stringToUserRole(role) !== undefined
}

/**
 * Type guards for safely accessing file content
 */

/**
 * Type guard to check if a plugin file has transpiled content
 * @param file The plugin file to check
 * @returns true if the file has transpiled content
 */
export function hasTranspiledContent(
  file?: PluginFile | null,
): file is PluginFile & { transpiledContent: string } {
  return (
    !!file &&
    typeof file.transpiledContent === "string" &&
    file.transpiledContent.length > 0
  )
}

/**
 * Type guard to check if a plugin has code structure
 * @param plugin The plugin to check
 * @returns true if the plugin has code structure
 */
export function hasCodeStructure(
  plugin?: Plugin | null,
): plugin is Plugin & { code: PluginCode } {
  return !!plugin && !!plugin.code
}

/**
 * Helper functions for working with user roles and views
 */

/**
 * Get the effective view role for a user, considering both role and viewAs properties
 * @param user The user to check
 * @returns The effective role for viewing content (viewAs if set, otherwise the user's role)
 */
export function getEffectiveViewRole(
  user?: TestCaseUser | null,
): UserRole | undefined {
  if (!user) return undefined
  return user.viewAs || user.role
}

/**
 * Check if a user can view content as a specific role
 * @param user The user to check
 * @param viewRole The role to check if user can view as
 * @returns true if the user can view as the specified role
 */
//TODO this function is ai generated and it didn't really understand the context of the codebase for this
// but this is a larger change for how we do roles and view to manage this
export function canViewAs(
  user?: TestCaseUser | null,
  viewRole?: UserRole,
): boolean {
  if (!user || !viewRole) return false

  if (user.role === UserRole.HOST_CONFIG_VIEW) return false

  // Host can view as host or guest
  if (user.role === UserRole.HOST_EVENT_VIEW) {
    return (
      viewRole === UserRole.HOST_EVENT_VIEW ||
      viewRole === UserRole.HOST_CONFIG_VIEW
    )
  }

  // Guest can only view as guest
  return user.role === viewRole
}

/**
 * Validation helpers for plugin data structures
 */

/**
 * Validates an event data object
 * @param data The event data to validate
 * @returns Array of validation errors, empty if valid
 */
export function validateEventData(data?: EventData): PluginError[] {
  const errors: PluginError[] = []

  if (!data) {
    errors.push({
      type: PluginErrorType.VALIDATION,
      message: "Event data is required",
      severity: ErrorSeverity.ERROR,
      timestamp: Date.now(),
    })
    return errors
  }

  // Validate required fields if needed
  if (
    data.date &&
    !(typeof data.date === "string" || data.date instanceof Date)
  ) {
    errors.push({
      type: PluginErrorType.VALIDATION,
      message: "Event date must be a string or Date object",
      severity: ErrorSeverity.WARNING,
      timestamp: Date.now(),
    })
  }

  // Add more validation rules as needed

  return errors
}

/**
 * Validates plugin configuration data
 * @param config The plugin configuration to validate
 * @returns Array of validation errors, empty if valid
 */
export function validatePluginConfig(config?: PluginConfigData): PluginError[] {
  const errors: PluginError[] = []

  if (!config) {
    errors.push({
      type: PluginErrorType.VALIDATION,
      message: "Plugin configuration is required",
      severity: ErrorSeverity.ERROR,
      timestamp: Date.now(),
    })
  }

  // Add specific validation rules based on expected configuration structure

  return errors
}

/**
 * Checks if a plugin file has valid content
 * @param file The plugin file to check
 * @returns true if the file has valid content
 */
export function hasValidContent(file?: PluginFile | null): boolean {
  return (
    !!file && typeof file.content === "string" && file.content.trim().length > 0
  )
}

/**
 * Deep clones plugin data to avoid mutation issues
 * @param data The data to clone
 * @returns A deep clone of the data
 */
export function clonePluginData<T>(data: T): T {
  return JSON.parse(JSON.stringify(data)) as T
}

//==============================================================================
// COMPONENT PROPS ORGANIZATION - USING PREFIXES INSTEAD OF NAMESPACES
//==============================================================================

/**
 * These interfaces demonstrate the proposed structure for cleaner organization
 * using prefixes instead of namespaces. These are examples and not yet implemented.
 */

// File Explorer related interfaces
export interface FileExplorerFolderProps {
  name: string
  path: string
  children: FileSystemItem[]
  isOpen: boolean
  onToggle: () => void
}

// Code Editor related interfaces - example for future implementations
export interface CodeEditorExtendedProps {
  activePlugin: Plugin | null
  activeFile: PluginFile | null
  viewRole: UserRole
  onCodeChange: (newCode: string) => void
  onViewRoleChange: (newRole: UserRole) => void
}

//==============================================================================
// PLUGIN OPERATION TYPES
//==============================================================================

/**
 * Result of a plugin operation with success/error status and metadata
 */
export interface PluginOperationResult<T = any> {
  success: boolean
  data?: T
  errors?: PluginError[]
  timestamp: number
  metadata?: {
    duration?: number
    operationType?: string
    [key: string]: any
  }
}

/**
 * Plugin save operation result
 */
export interface PluginSaveResult
  extends PluginOperationResult<{
    pluginId: string
    version?: string
    lastSaved: number
  }> {}

/**
 * Plugin transpile operation result
 */
export interface PluginTranspileResult
  extends PluginOperationResult<{
    fileIds: string[]
    transpiledContent: Record<string, string>
  }> {}

/**
 * Plugin test operation result
 */
export interface PluginTestResult
  extends PluginOperationResult<{
    testCaseId: string
    results: {
      passed: boolean
      performance?: {
        executionTime: number
        memoryUsage?: number
      }
      logs?: string[]
    }
  }> {}

/**
 * Plugin service interface - defines operations that can be performed on plugins
 */
export interface PluginService {
  // Plugin CRUD operations
  getPlugins(): Promise<PluginOperationResult<Plugin[]>>
  getPluginById(id: string): Promise<PluginOperationResult<Plugin>>
  createPlugin(plugin: Partial<Plugin>): Promise<PluginOperationResult<Plugin>>
  updatePlugin(
    id: string,
    updates: Partial<Plugin>,
  ): Promise<PluginOperationResult<Plugin>>
  deletePlugin(id: string): Promise<PluginOperationResult<void>>

  // Plugin file operations
  saveFile(
    pluginId: string,
    file: PluginFile,
  ): Promise<PluginOperationResult<PluginFile>>
  transpileFile(
    pluginId: string,
    fileId: string,
  ): Promise<PluginTranspileResult>

  // Plugin test operations
  runTest(pluginId: string, testCaseId: string): Promise<PluginTestResult>
  saveTestCase(
    pluginId: string,
    testCase: TestCase,
  ): Promise<PluginOperationResult<TestCase>>
}

//==============================================================================
// API REQUEST AND RESPONSE TYPES
//==============================================================================

/**
 * Base API request interface
 */
export interface ApiRequest {
  timestamp?: number
  requestId?: string
}

/**
 * Base API response interface
 */
export interface ApiResponse {
  success: boolean
  timestamp: number
  requestId?: string
  errors?: PluginError[]
}

/**
 * Plugin list request
 */
export interface GetPluginsRequest extends ApiRequest {
  filters?: {
    category?: string
    tags?: string[]
    search?: string
    limit?: number
    offset?: number
  }
}

/**
 * Plugin list response
 */
export interface GetPluginsResponse extends ApiResponse {
  plugins: Plugin[]
  total?: number
  page?: number
  pageSize?: number
}

/**
 * Single plugin request
 */
export interface GetPluginRequest extends ApiRequest {
  pluginId: string
}

/**
 * Single plugin response
 */
export interface GetPluginResponse extends ApiResponse {
  plugin: Plugin
}

/**
 * Create plugin request
 */
export interface CreatePluginRequest extends ApiRequest {
  plugin: Omit<Plugin, "id" | "createdAt">
}

/**
 * Update plugin request
 */
export interface UpdatePluginRequest extends ApiRequest {
  pluginId: string
  updates: Partial<Plugin>
}

/**
 * Save file request
 */
export interface SaveFileRequest extends ApiRequest {
  pluginId: string
  file: PluginFile
}

/**
 * Save file response
 */
export interface SaveFileResponse extends ApiResponse {
  file: PluginFile
  lastModified: number
}

/**
 * Transpile file request
 */
export interface TranspileFileRequest extends ApiRequest {
  pluginId: string
  fileId: string
}

/**
 * Transpile file response
 */
export interface TranspileFileResponse extends ApiResponse {
  fileId: string
  transpiledContent: string
  warnings?: string[]
}

/**
 * Run test request
 */
export interface RunTestRequest extends ApiRequest {
  pluginId: string
  testCaseId: string
}

/**
 * Run test response
 */
export interface RunTestResponse extends ApiResponse {
  testResults: {
    passed: boolean
    executionTime: number
    logs: string[]
    errors?: PluginError[]
    output?: any
  }
}

/**
 * Implementation of PluginService using the API
 */
export class ApiPluginService implements PluginService {
  // Base method for making API requests
  private async apiRequest<T extends ApiResponse>(
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    data?: any,
  ): Promise<T> {
    // This would be implemented to actually make the API calls
    // For now, it's just a placeholder
    throw new Error("Not implemented")
  }

  // Plugin CRUD operations
  async getPlugins(): Promise<PluginOperationResult<Plugin[]>> {
    try {
      const response = await this.apiRequest<GetPluginsResponse>(
        "/plugins",
        "GET",
      )
      return {
        success: response.success,
        data: response.plugins,
        errors: response.errors,
        timestamp: response.timestamp,
      }
    } catch (error) {
      return {
        success: false,
        errors: [
          {
            type: PluginErrorType.NETWORK,
            message: error instanceof Error ? error.message : "Unknown error",
            severity: ErrorSeverity.ERROR,
            timestamp: Date.now(),
          },
        ],
        timestamp: Date.now(),
      }
    }
  }

  // Other methods would be implemented similarly
  async getPluginById(id: string): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async createPlugin(
    plugin: Partial<Plugin>,
  ): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async updatePlugin(
    id: string,
    updates: Partial<Plugin>,
  ): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async deletePlugin(id: string): Promise<PluginOperationResult<void>> {
    throw new Error("Not implemented")
  }

  async saveFile(
    pluginId: string,
    file: PluginFile,
  ): Promise<PluginOperationResult<PluginFile>> {
    throw new Error("Not implemented")
  }

  async transpileFile(
    pluginId: string,
    fileId: string,
  ): Promise<PluginTranspileResult> {
    throw new Error("Not implemented")
  }

  async runTest(
    pluginId: string,
    testCaseId: string,
  ): Promise<PluginTestResult> {
    throw new Error("Not implemented")
  }

  async saveTestCase(
    pluginId: string,
    testCase: TestCase,
  ): Promise<PluginOperationResult<TestCase>> {
    throw new Error("Not implemented")
  }
}

/**
 * Mock implementation of PluginService for testing
 */
export class MockPluginService implements PluginService {
  // Mock data
  private plugins: Plugin[] = []
  private testCases: TestCase[] = []

  // Plugin CRUD operations
  async getPlugins(): Promise<PluginOperationResult<Plugin[]>> {
    return {
      success: true,
      data: this.plugins,
      timestamp: Date.now(),
    }
  }

  // Other methods would be implemented with mock functionality
  // ...

  // This is just a placeholder - implementations would be added as needed
  async getPluginById(id: string): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async createPlugin(
    plugin: Partial<Plugin>,
  ): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async updatePlugin(
    id: string,
    updates: Partial<Plugin>,
  ): Promise<PluginOperationResult<Plugin>> {
    throw new Error("Not implemented")
  }

  async deletePlugin(id: string): Promise<PluginOperationResult<void>> {
    throw new Error("Not implemented")
  }

  async saveFile(
    pluginId: string,
    file: PluginFile,
  ): Promise<PluginOperationResult<PluginFile>> {
    throw new Error("Not implemented")
  }

  async transpileFile(
    pluginId: string,
    fileId: string,
  ): Promise<PluginTranspileResult> {
    throw new Error("Not implemented")
  }

  async runTest(
    pluginId: string,
    testCaseId: string,
  ): Promise<PluginTestResult> {
    throw new Error("Not implemented")
  }

  async saveTestCase(
    pluginId: string,
    testCase: TestCase,
  ): Promise<PluginOperationResult<TestCase>> {
    throw new Error("Not implemented")
  }
}
